// src/utils/carteleria/filasGrillaCarteleria.js
//
// Filas de Cartelería (HW-CAPEX, HW-OPEX, Gestión) de la grilla de Facturación (`useVFDataLoading`).
// F3b del rediseño: los IMPORTES y los MESES salen del motor `calendarioNegocio` (líneas), la conversión por el
// helper único `convertirMontoCarteleria`. Reemplaza el bloque que armaba todo con fórmulas propias; el
// resultado es IDÉNTICO fila por fila al de antes (oráculo: `__tests__/oraculos/grillaCarteleriaAntes.js`).
//
// La grilla solo convierte UF → CLP (por el UF del día de factura); USD queda en dólares y se marca con
// `moneda_dolar`. Por eso su "conversor" es propio, pero pasa por el helper único.
import { calendarioNegocio } from "./calendarioNegocio";
import { convertirMontoCarteleria } from "./convertirMontoCarteleria";

const DESTINO_GRILLA = "__GRILLA__"; // la grilla no convierte a una moneda local: solo expande UF a CLP

/**
 * @param {Array} negociosCarteleria filas de `negocios_carteleria` del país
 * @param {object} ctx
 * @param {Object} ctx.clientesMap id → nombre
 * @param {Object} ctx.edificiosMap id → nombre
 * @param {Function} ctx.resolverLineaId (negocioId, clave) → linea_id | null
 * @param {Function} ctx.derivarClaveHardware (detalle, idx) → clave | null
 * @param {Function} ctx.getUFValue (fechaISO) → valor UF
 * @param {Date} [ctx.hoy] fecha de referencia (default: ahora)
 */
export const generarFilasCarteleriaGrilla = (
  negociosCarteleria,
  { clientesMap, edificiosMap, resolverLineaId, derivarClaveHardware, getUFValue, hoy = new Date() }
) => {
  const filas = [];
  const hoyISO = hoy.toISOString().split("T")[0];
  const aGrilla = (monto, moneda, fechaRef) =>
    convertirMontoCarteleria(
      (m, desde) => (desde === "UF" ? m * (getUFValue(fechaRef) || 1) : m),
      monto,
      moneda,
      DESTINO_GRILLA
    );
  // y, m de una fecha "YYYY-MM-DD" (o del año actual / enero si no hay fecha), como la grilla de siempre.
  const anoMes = (fecha) => {
    const [y, m] = fecha ? fecha.split("-") : [hoy.getFullYear().toString(), "01"];
    return { ano: y || hoy.getFullYear().toString(), mes: (m || "01").padStart(2, "0") };
  };

  negociosCarteleria
    .filter((n) => ["activo", "100%"].includes((n.estado || "").toLowerCase()))
    .forEach((n) => {
      const cal = calendarioNegocio(n, { hoy });
      const pantallas = cal.pantallas;
      const nombreCliente =
        n.tipo_vertical === "TD"
          ? clientesMap[n.referencia_id] || edificiosMap[n.referencia_id] || n.nombre_proyecto || "—"
          : edificiosMap[n.referencia_id] || clientesMap[n.referencia_id] || n.nombre_proyecto || "—";
      const ejecutivoId = n.ejecutivo ? parseInt(n.ejecutivo, 10) : null;
      const mesesFact = Array.isArray(n.meses_facturacion)
        ? n.meses_facturacion
        : typeof n.meses_facturacion === "string"
          ? JSON.parse(n.meses_facturacion)
          : [];
      const idxEnMesesFact = (tipo, ano, mes) =>
        mesesFact.findIndex((mf) => mf.mes === mes && mf.ano === ano && mf.tipo === tipo);

      // Valores por pantalla tal como se cargaron (dato, no cálculo) — para el prorrateo SII (feeProporcion).
      const feeGestionNeg = cal.porPantalla.gestion.monto;
      const feeOpexNeg = cal.porPantalla.opex.monto;
      const feeContNeg = cal.porPantalla.contenidos.monto;

      const detalle = (() => {
        const d = n.detalle_pagos_hardware;
        return typeof d === "string" ? JSON.parse(d || "[]") : d || [];
      })();

      // ── HW-CAPEX: una fila por tramo (o "Pago único") — líneas de hardware del motor ──────────────
      cal.lineas
        .filter((l) => l.tipo === "hardware_capex")
        .forEach((l) => {
          const idx = l.clave.tramo;
          const unico = detalle.length === 0; // "Pago único": CAPEX sin tramos, con monto_hardware
          // Fecha que ubica el UF del día: la de facturación del tramo (o la de cierre/inicio del pago único).
          const fechaRef = unico ? n.fecha_cierre || n.fecha_inicio || null : l.facturafecha;
          // Mes de la grilla: cascada del motor (facturafecha → estimada → cierre → inicio); sin ninguna, enero.
          const { ano, mes } = anoMes(l.mes ? `${l.mes}-01` : null);
          const importe = aGrilla(l.monto, l.moneda, fechaRef || hoyISO);
          filas.push({
            id: `hw-capex-${n.id}-${idx}`,
            negocio_id: n.id,
            display_id: n.id,
            entry_index: idx,
            sii_ref_index: idx,
            linea_id: unico ? null : resolverLineaId(n.id, derivarClaveHardware(detalle, idx)),
            client_id: null,
            importe,
            comision: null,
            facturafecha: unico ? null : l.facturafechaExplicita,
            facturanumero: unico ? null : l.facturanumero,
            facturafechacobro: unico ? null : l.facturafechacobro,
            plazocobro: unico ? null : l.plazocobro,
            mes,
            ano,
            estatus: "100%",
            eliminada: false,
            cliente: nombreCliente,
            type: "HW-CAPEX",
            moneda_dolar: l.moneda === "USD",
            ejecutivo: ejecutivoId,
            label_extra: l.etiqueta,
            valor_cuota: l.monto,
            moneda_cuota: l.moneda,
          });
        });

      // ── HW-OPEX: una fila por mes del contrato ───────────────────────────────────────────────────
      cal.lineas
        .filter((l) => l.tipo === "opex")
        .forEach((l) => {
          const [anoStr, mesStr] = l.mes.split("-");
          const mesFactIdx = idxEnMesesFact("opex", anoStr, mesStr);
          const fechaRef = l.facturafecha || `${anoStr}-${mesStr}-01`;
          const totalFeeNeg = (feeOpexNeg + feeGestionNeg + feeContNeg) * pantallas;
          const feeProporcion = totalFeeNeg > 0 ? l.monto / totalFeeNeg : 1;
          filas.push({
            id: `hw-opex-${n.id}-${l.indice}`,
            negocio_id: n.id,
            display_id: n.id,
            mes_index: l.indice,
            sii_ref_index: mesFactIdx >= 0 ? mesFactIdx : null,
            linea_id: resolverLineaId(n.id, l.clave),
            client_id: null,
            importe: aGrilla(l.monto, l.moneda, fechaRef),
            comision: null,
            feeOpexUF: l.monto,
            feeProporcion,
            facturafecha: l.facturafecha,
            facturanumero: l.facturanumero,
            facturafechacobro: l.facturafechacobro,
            plazocobro: l.plazocobro,
            mes: mesStr,
            ano: anoStr,
            estatus: "100%",
            eliminada: false,
            cliente: nombreCliente,
            type: "HW-OPEX",
            moneda_dolar: l.moneda === "USD",
            ejecutivo: ejecutivoId,
            label_extra: l.etiqueta,
            valor_cuota: l.monto,
            moneda_cuota: l.moneda,
          });
        });

      // ── Gestión & Contenidos: una fila por mes (solo EO/TD, no OT) ───────────────────────────────
      cal.lineas
        .filter((l) => l.tipo === "gestion")
        .forEach((l) => {
          const [anoStr, mesStr] = l.mes.split("-");
          const mesFactIdx = idxEnMesesFact("gestion", anoStr, mesStr);
          const fechaRef = l.facturafecha || `${anoStr}-${mesStr}-01`;
          const enRangoContenidos = !!l.contenidos;
          const contMonto = enRangoContenidos ? l.contenidos.monto : 0;
          const importeGestion = aGrilla(l.monto, l.moneda, fechaRef);
          // Contenidos: solo se convierte si su moneda CARGADA es UF (sin el respaldo a la de gestión).
          const importeContenidos = enRangoContenidos
            ? aGrilla(contMonto, cal.monedas.contenidosCargada, fechaRef)
            : contMonto;
          const feeTotal = l.monto + contMonto;
          const totalFeeNeg = (feeOpexNeg + feeGestionNeg + (enRangoContenidos ? feeContNeg : 0)) * pantallas;
          const feeProporcion = totalFeeNeg > 0 ? feeTotal / totalFeeNeg : 1;
          filas.push({
            id: `gestion-${n.id}-${l.indice}`,
            negocio_id: n.id,
            display_id: n.id,
            mes_index: l.indice,
            sii_ref_index: mesFactIdx >= 0 ? mesFactIdx : null,
            linea_id: resolverLineaId(n.id, l.clave),
            client_id: null,
            importe: importeGestion + importeContenidos,
            comision: null,
            importeGestion,
            importeContenidos,
            feeProporcion,
            feeGestionUF: l.moneda === "UF" ? l.monto : null,
            feeContenidosUF: enRangoContenidos && cal.monedas.contenidosCargada === "UF" ? contMonto : null,
            feeContenidosCLP: enRangoContenidos && cal.monedas.contenidosCargada === "CLP" ? contMonto : null,
            facturafecha: l.facturafecha,
            facturanumero: l.facturanumero,
            facturafechacobro: l.facturafechacobro,
            plazocobro: l.plazocobro,
            mes: mesStr,
            ano: anoStr,
            estatus: "100%",
            eliminada: false,
            cliente: nombreCliente,
            type: "Gestión",
            moneda_dolar: l.moneda === "USD",
            ejecutivo: ejecutivoId,
            label_extra: l.etiqueta,
            valor_cuota: feeTotal,
            moneda_cuota: n.moneda_gestion,
            incluye_contenidos: enRangoContenidos,
          });
        });
    });
  return filas;
};
